// src/components/landing/DocumentReviews/index.tsx
'use client';

import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, Container, Group, Stack, Text, Title } from '@mantine/core';
import Image from 'next/image';
import { useResponsive } from '@/hooks/use-responsive';
import { DocumentReviewsContent } from '@/utils/types/document-reviews';
import { MarkdownContent } from '@/app/page';

// Define proper interface for styled component props
interface RootProps {
  $content?: DocumentReviewsContent;
  className?: string;
}

const Root = styled('div') <RootProps>`
  ${({ $content }) => css``}
`;

export interface DocumentReviewsProps {
  className?: string;
  markdownContent?: MarkdownContent;
}

export default function DocumentReviews({ className, markdownContent }: DocumentReviewsProps) {
  // const { content } = useDocumentReviewsContent();
  const content = markdownContent?.frontmatter;
  const { responsive } = useResponsive();

  useEffect(() => {
    // console.log('Document Reviews Markdown Content:', markdownContent);
  }, [markdownContent]);

  if (!content) return null;

  return (
    <Root
      $content={content as DocumentReviewsContent}
      className={className}
      style={{
        padding: responsive({
          xs: content.layout.padding.xs,
          lg: content.layout.padding.lg,
        }),
        maxHeight: responsive({
          lg: content.layout.maxHeight
        })
      }}
    >
      <Container size="xl">
        <Group
          gap={responsive({
            xs: content.layout.gap.xs,
            md: content.layout.gap.md,
            lg: content.layout.gap.lg,
            xl: content.layout.gap.xl,
          })}
          style={{
            flexDirection: responsive({
              xs: 'column',
              lg: 'row',
            }),
            flexWrap: 'nowrap',
          }}
        >
          <Box
            flex={1}
            display={responsive({
              xs: 'none',
              lg: 'flex',
            })}
          >
            <Image
              src={content.image.src}
              width={content.image.width}
              height={content.image.height}
              alt={content.image.alt}
              style={{
                width: '100%',
                maxHeight: `${content.image.height}px`,
                objectFit: 'cover',
              }}
              layout="responsive"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              quality={75}
            />
          </Box>

          <Stack
            flex={1}
            style={{
              order: responsive({
                xs: -1,
                lg: 1,
              }),
              gap: responsive({
                xs: 10,
                lg: 15,
              }),
            }}
          >
            <Title
              size={responsive({
                xs: content.typography.title.xs,
                xl: content.typography.title.xl,
              })}
              fw={600}
            >
              {content.title}
            </Title>

            <Text
              fz={responsive({
                xs: content.typography.description.xs,
                xl: content.typography.description.xl,
              })}
              fw={300}
            >
              {content.description}
            </Text>

            <Group
              wrap="nowrap"
              gap={20}
              mt={20}
              style={{
                flexDirection: responsive({
                  xs: 'column',
                  sm: 'row',
                }),
              }}
            >
              <Image
                src={content.image.src}
                width={content.image.width}
                height={320}
                alt={content.image.alt}
                style={{
                  display: responsive({
                    xs: 'block',
                    lg: 'none',
                  }),
                  order: responsive({
                    xs: 1,
                    sm: -1,
                  }),
                  width: '100%',
                  maxHeight: '320px',
                  objectFit: 'cover',
                }}
                layout="responsive"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                quality={75}
              />

              <Group
                justify="space-between"
                wrap="nowrap"
                gap={15}
                align="flex-start"
                style={{
                  flexDirection: responsive({
                    xs: 'column',
                    lg: 'row',
                  }),
                }}
              >
                {content.features.map((feature: { title: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | React.PromiseLikeOfReactNode | null | undefined; description: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | React.PromiseLikeOfReactNode | null | undefined; }, index: React.Key | null | undefined) => (
                  <Stack key={index} gap={8}>
                    <Title
                      size={content.typography.feature.size}
                      fw={content.typography.feature.weight}
                    >
                      {feature.title}
                    </Title>
                    <Text c="dark.3" fz="sm">
                      {feature.description}
                    </Text>
                  </Stack>
                ))}
              </Group>
            </Group>
          </Stack>
        </Group>
      </Container>
    </Root>
  );
}